import {Children, Fragment, cloneElement, forwardRef, type ElementType, type ReactElement, type Ref} from "react";
import {cn} from "../lib/utils";
import {responsive, splitSpacing, type ResponsiveValue, type Spacing, type SpacingProps} from "../lib/responsive";
import {splitBorder, type BorderProps} from "../lib/border";
import {stateful, type PaletteColor, type StatefulValue} from "../lib/color";
import {Separator} from "../Separator";
import type {PolymorphicComponent, PolymorphicProps} from "../lib/polymorphic";

const StackInner = forwardRef(<E extends ElementType = "div">(props: StackProps<E>, ref: Ref<Element>) => {
	const {className: spacingClassName, rest: withoutSpacing} = splitSpacing(props);
	const {className: borderClassName, rest} = splitBorder(withoutSpacing);
	const {
		as,
		direction = "row",
		gap = 2,
		alignItems = "center",
		justifyContent = "flexStart",
		justifySelf,
		alignSelf,
		backgroundColor,
		fullWidth = false,
		queryContainer = false,
		divider,
		className,
		children,
		...domProps
	} = rest;

	// One cast so the JSX below stays readable — E is only knowable at the call site.
	const Component = (as ?? "div") as ElementType;
	const items = Children.toArray(children);
	const dividerElement = (divider === true ? <Separator/> : divider || undefined) as
		ReactElement<Record<string, unknown>> | undefined;
	const separated = dividerElement !== undefined && items.length > 1;

	return (
		<Component
			ref={ref}
			data-slot="stack"
			{...(Component === "button" && !("type" in domProps) ? {type: "button"} : {})}
			className={cn(
				"flex",
				queryContainer && "@container",
				responsive(direction, directionClass),
				responsive(gap, value => `gap-${value}`),
				responsive(alignItems, alignItemsClass),
				responsive(justifyContent, justifyContentClass),
				responsive(justifySelf, value => `justify-self-${value}`),
				responsive(alignSelf, value => `self-${value}`),
				spacingClassName,
				borderClassName,
				stateful(backgroundColor, value => `bg-${value}`),
				fullWidth && "w-full",
				className
			)}
			{...domProps}>
			{separated
				? items.map((child, index) =>
					<Fragment key={index}>
						{child}
						{index < items.length - 1 && cloneElement(dividerElement, {
							orientation: dividerOrientation(direction),
							...dividerElement.props as object
						})}
					</Fragment>)
				: children}
		</Component>
	);
});

export const Stack = StackInner as PolymorphicComponent<StackOwnProps, "div">;

Stack.displayName = "Stack";

function dividerOrientation(direction: ResponsiveValue<StackDirection>) {
	const base = typeof direction === "object" ? direction.default ?? "row" : direction;
	return base === "row" || base === "rowReverse" ? "vertical" : "horizontal";
}

const directionClass = (direction: StackDirection) => ({
	row: "flex-row",
	column: "flex-col",
	rowReverse: "flex-row-reverse",
	columnReverse: "flex-col-reverse"
}[direction]);

const alignItemsClass = (align: StackAlignItems) => ({
	flexStart: "items-start",
	center: "items-center",
	flexEnd: "items-end",
	stretch: "items-stretch",
	baseline: "items-baseline"
}[align]);

const justifyContentClass = (justify: StackJustifyContent) => ({
	flexStart: "justify-start",
	center: "justify-center",
	flexEnd: "justify-end",
	spaceBetween: "justify-between",
	spaceAround: "justify-around",
	spaceEvenly: "justify-evenly"
}[justify]);

export type StackDirection = "row" | "column" | "rowReverse" | "columnReverse";

export type StackAlignItems = "flexStart" | "center" | "flexEnd" | "stretch" | "baseline";

export type StackJustifyContent = "flexStart" | "center" | "flexEnd" | "spaceBetween" | "spaceAround" | "spaceEvenly";

export type StackJustifySelf = "auto" | "start" | "end" | "center" | "stretch";

export type StackAlignSelf = "auto" | "start" | "end" | "center" | "stretch" | "baseline";

export interface StackOwnProps extends SpacingProps, BorderProps {
	direction?: ResponsiveValue<StackDirection>;
	gap?: ResponsiveValue<Spacing>;
	alignItems?: ResponsiveValue<StackAlignItems>;
	justifyContent?: ResponsiveValue<StackJustifyContent>;
	justifySelf?: ResponsiveValue<StackJustifySelf>;
	alignSelf?: ResponsiveValue<StackAlignSelf>;
	backgroundColor?: StatefulValue<PaletteColor>;
	fullWidth?: boolean;
	queryContainer?: boolean;
	divider?: boolean | ReactElement;
}

export type StackProps<E extends ElementType = "div"> = PolymorphicProps<E, StackOwnProps>;
